import { snapdom } from "@zumer/snapdom";
import bs58 from "bs58";
import QRCode from "qrcode";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import footerLogo from "@/assets/images/footer_logo.png";
import inviteDialogBg from "@/assets/images/invite_dialog_bg.png";
import { Icon } from "@/components/icon";
import SplitLine from "@/components/SplitLine";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useInviteDialogStore } from "@/store/inviteDialogStore";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/ui/sheet";
import { parseInviteCode } from "@/utils/inviteCode";

export default function InviteDialog() {
	const { t } = useTranslation();
	const { isOpen, inviteCode, poolName, closeInviteDialog } = useInviteDialogStore();
	const isMobile = useMediaQuery("(max-width: 768px)");

	const [inviteLink, setInviteLink] = useState("");
	const [qrCodeUrl, setQrCodeUrl] = useState("");

	// 生成邀请链接和二维码
	useEffect(() => {
		if (isOpen && inviteCode) {
			// 生成邀请链接
			const poolId = parseInviteCode(inviteCode)?.poolId;
			const link = `${window.location.origin}/mining/pool/${poolId}`;
			setInviteLink(link);

			// 生成二维码
			QRCode.toDataURL(link, {
				width: 200,
				margin: 1,
				color: {
					dark: "#000000",
					light: "#FFFFFF",
				},
			})
				.then((url) => setQrCodeUrl(url))
				.catch((err) => console.error("Failed to generate QR code:", err));
		}
	}, [isOpen, inviteCode]);

	// 复制到剪贴板
	const handleCopy = async (text: string, type: "code" | "link") => {
		try {
			await navigator.clipboard.writeText(text);
			toast.success(
				type === "code" ? t("portfolio.invite.dialog.codeCopied") : t("portfolio.invite.dialog.linkCopied"),
			);
		} catch (error) {
			console.error("Failed to copy:", error);
			toast.error(t("portfolio.invite.dialog.copyFailed"));
		}
	};

	// 分享到 X (Twitter)
	const handleShareToX = () => {
		const text = `Join FOMO100 and earn rewards! Use my referral code: ${inviteCode}`;
		const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(inviteLink)}`;
		window.open(url, "_blank");
	};

	// 分享到 Telegram
	const handleShareToTelegram = () => {
		const text = `Join FOMO100 and earn rewards! Use my referral code: ${inviteCode}\n${inviteLink}`;
		const url = `https://t.me/share/url?url=${encodeURIComponent(inviteLink)}&text=${encodeURIComponent(text)}`;
		window.open(url, "_blank");
	};

	// 保存二维码
	const handleSaveQRCode = async () => {
		if (!qrCodeUrl) return;
		const bannerEl = document.getElementById("invite-dialog-banner");
		if (!bannerEl) {
			toast.error("无法找到 banner 元素");
			return;
		}

		const result = await snapdom(bannerEl as Element);

		const img = await result.toPng();
		document.body.appendChild(img);

		await result.download({ format: "jpg", filename: `invite-qrcode-${inviteCode}` });
		document.body.removeChild(img);
		toast.success(t("portfolio.invite.dialog.qrCodeSaved"));
	};

	// 共用的内容
	const content = (
		<div className={isMobile ? "px-4" : "px-8 w-full"}>
			{/* Banner with QR Code */}
			<div className="mb-6 relative bg-black overflow-hidden" id="invite-dialog-banner">
				<img src={inviteDialogBg} alt="" className="w-full h-auto pb-[40px] max-md:pb-[60px]" />
				<div className="absolute inset-0 p-4 flex flex-col justify-between">
					<div className="flex flex-col h-full">
						<div className="flex items-center gap-2 mb-2">
							<img src={footerLogo} alt="" className="h-[32px]" />
						</div>
						<h2 className={`text-white ${isMobile ? "text-[24px]" : "text-[24px]"} font-normal mb-2 mt-[96px]`}>
							{t("portfolio.invite.dialog.bannerTitle", { poolName: poolName })}
						</h2>
						<p
							className={`text-white ${isMobile ? "text-[24px]" : "text-[24px]"} font-bold italic tracking-[-0.06rem]`}
						>
							{t("portfolio.invite.dialog.bannerSubtitle")}
						</p>
					</div>
					<div
						className="bg-[#B9FF00] pl-4 pr-[32px] flex items-center justify-between gap-4 py-[8px] h-[80px] absolute bottom-0 right-0 left-0"
						style={{ clipPath: "polygon(91% 0, 100% 30%, 100% 100%, 0 100%, 0 0)" }}
					>
						<div className="flex flex-col">
							<span className="text-sm text-gray-600 block mb-1">{t("portfolio.invite.dialog.referralCode")}</span>
							<span className="text-xl font-bold text-black break-all">{inviteCode || "Loading..."}</span>
						</div>
						{/* QR Code */}
						<div className="flex-shrink-0">
							{qrCodeUrl ? (
								<img src={qrCodeUrl} alt="QR Code" className="w-[60px] h-[60px]" />
							) : (
								<div className="w-[120px] h-[120px] flex items-center justify-center bg-gray-100">
									<Icon icon="mdi:loading" size={32} className="animate-spin text-gray-400" />
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* Referral Code */}
			<div className="mb-4">
				<div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
					<div>
						<span className="text-sm text-gray-600 block mb-1">{t("portfolio.invite.dialog.referralCode")}</span>
						<span className="text-xl font-bold text-black">{inviteCode || "Loading..."}</span>
					</div>
					<button
						type="button"
						onClick={() => handleCopy(inviteCode, "code")}
						className="p-2 hover:bg-gray-200 rounded transition-colors"
						disabled={!inviteCode}
					>
						<Icon icon="mdi:content-copy" size={20} className="text-gray-700" />
					</button>
				</div>
			</div>

			{/* Referral Link */}
			{/* <div className="mb-6">
				<div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
					<div className="flex-1 min-w-0 mr-3">
						<span className="text-sm text-gray-600 block mb-1">{t("portfolio.invite.dialog.referralLink")}</span>
						<span className="text-sm font-medium text-black truncate block">{inviteLink || "Loading..."}</span>
					</div>
					<button
						type="button"
						onClick={() => handleCopy(inviteLink, "link")}
						className="p-2 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
						disabled={!inviteLink}
					>
						<Icon icon="mdi:content-copy" size={20} className="text-gray-700" />
					</button>
				</div>
			</div> */}

			{/* Share Buttons */}
			<div className="grid grid-cols-3 gap-4 mb-6">
				<div className="flex flex-col flex-1 items-center gap-2">
					<button
						type="button"
						onClick={handleShareToX}
						className="flex flex-col w-full items-center gap-2 p-4 bg-black rounded-lg hover:bg-gray-800 transition-colors"
						disabled={!inviteCode}
					>
						<Icon icon="local:twitter" size={24} color="white" />
					</button>

					<span className="text-sm text-[#1F1F1F] font-medium">X</span>
				</div>
				<div className="flex flex-col flex-1 items-center gap-2">
					<button
						type="button"
						onClick={handleShareToTelegram}
						className="flex flex-col w-full items-center gap-2 p-4 bg-[#0088cc] rounded-lg hover:bg-[#0077b5] transition-colors"
						disabled={!inviteCode}
					>
						<Icon icon="mdi:telegram" size={24} color="white" />
					</button>
					<span className="text-sm text-[#1F1F1F] font-medium">Telegram</span>
				</div>

				<div className="flex flex-col flex-1 items-center gap-2">
					<button
						type="button"
						onClick={handleSaveQRCode}
						className="flex flex-col w-full items-center gap-2 p-4 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
						disabled={!qrCodeUrl}
					>
						<Icon icon="mdi:download" size={24} className="text-gray-700" />
					</button>
					<span className="text-sm text-[#1F1F1F] font-medium">Save</span>
				</div>
			</div>
		</div>
	);

	// 移动端使用 Sheet
	if (isMobile) {
		return (
			<Sheet open={isOpen} onOpenChange={closeInviteDialog}>
				<SheetContent side="bottom" className="h-[90vh] rounded-t-[20px] overflow-y-auto p-0 max-w-full w-[100vw]">
					<div className="px-4 pt-10 pb-2">
						<SplitLine className="mb-1 w-full" />
						<SheetHeader className="p-0">
							<SheetTitle className="text-xl font-medium uppercase">{t("portfolio.invite.dialog.title")}</SheetTitle>
						</SheetHeader>
					</div>
					{content}
				</SheetContent>
			</Sheet>
		);
	}

	// 桌面端使用 Dialog
	return (
		<Dialog open={isOpen} onOpenChange={closeInviteDialog}>
			<DialogContent className="w-full p-0 bg-white rounded-[20px] overflow-hidden">
				{/* Header */}
				<div className="px-8 pt-8 pb-4 w-full">
					<SplitLine className="mb-1" />
					<DialogHeader>
						<DialogTitle className="text-[24px] font-medium uppercase">
							{t("portfolio.invite.dialog.title")}
						</DialogTitle>
					</DialogHeader>
				</div>
				{content}
			</DialogContent>
		</Dialog>
	);
}
